import { DataGridState } from "../_models/data-grid-state";
import { alert } from "devextreme/ui/dialog";
import { CommonStorage } from "../_services/storage";
import { nextTick } from "vue";

/*
  This function is used to update the selected view definition in local storage while maintaining the selected definition data in the Vuex state.
  @param {object} state - The current state of the Vuex store module.
  @param {string} key - The key for which the selected view definition needs to be updated.
  @param {object} viewDefinition - The view definition object that needs to be set as selected.
*/
function updateSelectedDefinition(state, key, viewDefinition) {
  state.selectedDefinition[key] = viewDefinition;

  let savedSelected = CommonStorage.getItems("Selected-viewDefinitions");

  if (savedSelected) {
    savedSelected = JSON.parse(savedSelected);
    savedSelected[key] = { RecordID: viewDefinition.RecordID };
    CommonStorage.saveItems("Selected-viewDefinitions", JSON.stringify(savedSelected));
  } else {
    CommonStorage.saveItems("Selected-viewDefinitions", JSON.stringify({ [key]: { RecordID: viewDefinition.RecordID } }));
  }
}

export const gridViewDefinition = {
  namespaced: true,
  state: {
    viewDefinition: {},
    originalColumns: {},
    selectedDefinition: {},
    changeRequire: null,
    triggerViewDefinitionChange: false,
  },
  actions: {
    async setGridViewDefinition({ commit }, key) {
      try {
        const response = await DataGridState.getGridViewDefinitionFromDatabase(key);
        if (response && response.data && response.data.length > 0) {
          commit("setGridViewDefinition", {
            key: key,
            viewDefinition: response.data,
          });
        } else {
          commit("setGridViewDefinition", {
            key: key,
            viewDefinition: [],
          });

          // If there is no saved definition, we can initialize the database with default values (as needed)
          await DataGridState.saveStateAndFilterToDatabase(
            key, // GridKey
            "Default", // TemplateName - setting default to Default, as there is no definition saved for the user, so it will be considered as default definition. This can be changed as per the requirement.
            "Everyone", // TemplateType - setting default to Everyone, as there is no definition saved for the user, so it will be available for all users. This can be changed as per the requirement.
            "New", // SaveType - setting default to New, as there is no definition saved for the user, so it will be considered as new definition. This can be changed as per the requirement.
            this.state.gridViewDefinition.originalColumns[key] || [], // Original columns (Unchanged by user) 
            null, // RecordID - setting default to null, as there is no definition saved for the user, so there is no existing record in the database. This can be changed as per the requirement.
            'Y' // IsDefault - setting default to Y, as there is no definition saved for the user, so it will be considered as default definition. This can be changed as per the requirement.
          );

        }

      } catch (error) {
        alert("Error while retrieving saved grid view definitions.", "Server Error", {
          confirmButtonText: "OK",
        });
        commit("setGridViewDefinition", {
          key: key,
          viewDefinition: [],
        });
      } finally {
        // Reset changeRequire flag after loading definitions
        commit("setChangeRequire", new Date().getTime());
      }
    },
    updateDefinitionTemplateName({ commit }, { key, RecordID, templateName }) {
      commit("updateDefinitionTemplateName", {
        key: key,
        RecordID: RecordID,
        templateName: templateName,
      });
    },
    updateGridViewDefinition({ commit }, { key, viewDefinition }) {
      commit("updateGridViewDefinition", {
        key: key,
        viewDefinition: viewDefinition,
      });
    },
    setOriginalColumns({ commit }, {
      key, columns
    }) {
      commit("setOriginalColumns", {
        key: key,
        columns: columns,
      });
    },
    setChangeRequire({ commit }, value) {
      commit("setChangeRequire", value);
    },
    setSelectedDefinition({ commit }, {
      key, viewDefinition
    }) {
      DataGridState.setSelectedTemplate(key, {
        "GridViewDefinitionRecordId": viewDefinition.RecordID,
      });

      commit("setSelectedDefinition", {
        key: key,
        viewDefinition: viewDefinition,
      });
    },
    setTriggerViewDefinitionChange({ commit }, value) {
      commit("setTriggerViewDefinitionChange", value);
    },
    updateDeleteDefinition({ commit }, { key, viewDefinition }) {
      commit("updateDeleteDefinition", {
        key: key,
        viewDefinition: viewDefinition,
      });
    },
    updateDefinitionOrder({ commit }, { key, definitionListOrdered }) {
      commit("updateDefinitionOrder", {
        key: key,
        definitionListOrdered: definitionListOrdered,
      });
    }
  },
  mutations: {
    updateDefinitionOrder(state, { key, definitionListOrdered }) {
      if (state.viewDefinition[key]) {
        // state.viewDefinition[key] need to match agains it's RecordID with definitionListOrdered's RecordID and update the Sequence as per the order in definitionListOrdered
        definitionListOrdered.forEach((orderedDef,) => {
          const defIndex = state.viewDefinition[key].findIndex(def => def.RecordID === orderedDef.RecordId);
          if (defIndex !== -1) {
            state.viewDefinition[key][defIndex].Sequence = orderedDef.Sequence;
          }
        });

        // state.viewDefinition[key] needs to be sorted based on the updated Sequence
        state.viewDefinition[key].sort((a, b) => a.Sequence - b.Sequence);

      }
      state.changeRequire = new Date().getTime();
    },
    updateDeleteDefinition(state, { key, viewDefinition }) {
      if (state.viewDefinition[key]) {
        const index = state.viewDefinition[key].findIndex(def => def.RecordID === viewDefinition.RecordID);
        if (index !== -1) {
          state.viewDefinition[key].splice(index, 1);
          // If the deleted definition is currently selected, clear the selection
          if (state.selectedDefinition[key] && state.selectedDefinition[key].RecordID === viewDefinition.RecordID) {
            state.selectedDefinition[key] = {};
            CommonStorage.saveItems("Selected-viewDefinitions", JSON.stringify({ ...JSON.parse(CommonStorage.getItems("Selected-viewDefinitions") || "{}"), [key]: {} }));

            if (state.viewDefinition[key].length === 0) {
              location.reload(); // If there is no definition left for the key, reload the page to reset the state. this will create a new default definition for the user when the page is reloaded, as there is no definition available in the database for the user.
            } else {
              // once selected definition is cleared, set selected to 1st available definition
              const firstAvailableDefinition = state.viewDefinition[key][0] || {};
              DataGridState.setSelectedTemplate(key, {
                "GridViewDefinitionRecordId": firstAvailableDefinition.RecordID,
              });
              updateSelectedDefinition(state, key, firstAvailableDefinition || {});


              nextTick(() => {
                state.triggerViewDefinitionChange = true;
              });
            }

          }
        }
      }
      state.changeRequire = new Date().getTime();
    },
    updateGridViewDefinition(state, { key, viewDefinition }) {
      // this time viewDefinition only contains one element, therefore viewDefinition needs to be updates as per that.
      if (state.viewDefinition[key]) {
        if (state.viewDefinition[key].length > 0) {
          const index = state.viewDefinition[key].findIndex(def => def.RecordID === viewDefinition.RecordID);
          if (index !== -1) {
            // Update existing definition
            state.viewDefinition[key][index] = viewDefinition;
          } else {
            // Add new definition
            state.viewDefinition[key].push(viewDefinition);
          }
        } else {
          // If there are no existing definitions for the key, simply add the new definition
          state.viewDefinition[key].push(viewDefinition);
          // state.selectedDefinition[key] = viewDefinition || {};
          updateSelectedDefinition(state, key, viewDefinition || {});
        }
      } else {
        // If the key does not exist, create a new array with the viewDefinition
        state.viewDefinition[key] = [viewDefinition];
        // state.selectedDefinition[key] = viewDefinition || {};
        updateSelectedDefinition(state, key, viewDefinition || {});
      }

      state.changeRequire = new Date().getTime();
    },
    setGridViewDefinition(state, { key, viewDefinition }) {
      state.viewDefinition[key] = viewDefinition;
      const IsSelected = viewDefinition.find(def => def.IsSelected);
      // state.selectedDefinition[key] = IsSelected || viewDefinition[0] || {};
      const selectionDefinition = IsSelected || viewDefinition[0] || {};
      let allowSelectionUpdate = true;

      let savedSelected = CommonStorage.getItems(
        "Selected-viewDefinitions",
      );

      if (savedSelected) {
        savedSelected = JSON.parse(savedSelected);
      } else {
        savedSelected = {};
      }

      if (
        savedSelected &&
        selectionDefinition &&
        savedSelected[key] &&
        savedSelected[key].RecordID ===
        selectionDefinition.RecordID
      ) {
        allowSelectionUpdate = false;
      }

      updateSelectedDefinition(state, key, selectionDefinition);

      if (IsSelected && allowSelectionUpdate) {
        state.triggerViewDefinitionChange = true;
      }
    },
    setOriginalColumns(state, {
      key, columns
    }) {
      state.originalColumns[key] = columns;
    },
    setChangeRequire(state, value) {
      state.changeRequire = value;
    },
    setSelectedDefinition(state, { key, viewDefinition }) {
      // state.selectedDefinition[key] = viewDefinition;


      updateSelectedDefinition(state, key, viewDefinition);
      state.changeRequire = new Date().getTime();
    },
    setTriggerViewDefinitionChange(state, value) {
      state.triggerViewDefinitionChange = value;
    },

    updateDefinitionTemplateName(state, { key, RecordID, templateName }) {
      if (state.viewDefinition[key]) {
        const index = state.viewDefinition[key].findIndex(def => def.RecordID === RecordID);
        if (index !== -1) {
          state.viewDefinition[key][index].TemplateName = templateName;

          nextTick(() => {
            state.changeRequire = new Date().getTime();
          });
        }
      }
    },
  },
  getters: {
    getGridViewDefinition: (state) => {
      return state.viewDefinition;
    },
    getOriginalColumns: (state) => {
      return state.originalColumns;
    },
    getGridViewDefinitionByKey: (state) => (key) => {
      return state.viewDefinition[key];
    },
    getOriginalColumnsByKey: (state) => (key) => {
      return state.originalColumns[key];
    },
    getSelectedDefinitionByKey: (state) => (key) => {
      return state.selectedDefinition[key];
    },
    getChangeRequire: (state) => {
      return state.changeRequire;
    },
    getTriggerViewDefinitionChange: (state) => {
      return state.triggerViewDefinitionChange;
    },
    getDefinitionWithSameKeyAndName: (state) => (key, TemplateName, TemplateType, selectedValue) => {
      if (state.viewDefinition[key]) {
        return state.viewDefinition[key].find(def => def.TemplateName === TemplateName && def.TemplateType === TemplateType && def.RecordID !== selectedValue);
      }
      return null;
    }
  },
};
